import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '../../../hooks/customReduxHooks';
import {
    selectEwusPatientInsuranceDeclaration,
    selectEwusResult,
} from '../../../store/ewusSlice';
import { MedicationPackDto } from '../../../types/prescription';
import { DOSAGE_MODES, DosageModeOption } from '../../../validators/newPrescription';
import prescriptionsApi from '../../../api/prescriptions/prescriptionsApi';

type ChangeEventValue = ChangeEvent<{
    name?: string;
    value: unknown;
}>;

type FormikLike = {
    values: {
        medicinalProduct?: { id?: number | string } | null;
        medicationPackId?: number | string;
        paymentLevel?: string;
        dosageUnit?: string;
    };
    setFieldValue: (field: string, value: unknown) => void;
};

type UsePrescriptionFormSharedParams = {
    formik: FormikLike;
    medicationPacks: MedicationPackDto[];
    setMedicationPacks: (value: MedicationPackDto[]) => void;
};

const usePrescriptionFormShared = ({
    formik,
    medicationPacks,
    setMedicationPacks,
}: UsePrescriptionFormSharedParams) => {
    const ewusResult = useAppSelector(selectEwusResult);
    const patientDeclaredPublicInsurance = useAppSelector(
        selectEwusPatientInsuranceDeclaration,
    );

    const [isReimbursementDisabled, setIsReimbursementDisabled] = useState<boolean>(false);

    const shouldDisableReimbursement =
        ewusResult?.status !== true || patientDeclaredPublicInsurance === false;

    const selectedMedicationPack = useMemo(
        () => medicationPacks.find(pack => pack.id === formik.values?.medicationPackId),
        [medicationPacks, formik.values?.medicationPackId],
    );

    const medicationPackDosageUnits =
        selectedMedicationPack?.medicationPackDosageUnits ?? [];
    const hasPredefinedDosageUnits = medicationPackDosageUnits.length > 0;

    useEffect(() => {
        setIsReimbursementDisabled(shouldDisableReimbursement);
        if (shouldDisableReimbursement && formik.values.paymentLevel !== '0') {
            formik.setFieldValue('paymentLevel', '0');
        }
    }, [shouldDisableReimbursement, formik.values.paymentLevel]);

    useEffect(() => {
        if (!hasPredefinedDosageUnits || formik.values.dosageUnit === '') return;
        if (
            medicationPackDosageUnits.some(
                unit => unit.unit === formik.values.dosageUnit,
            )
        )
            return;
        formik.setFieldValue('dosageUnit', '');
    }, [hasPredefinedDosageUnits, medicationPackDosageUnits, formik.values.dosageUnit]);

    useEffect(() => {
        const medicinalProductId = Number(formik.values.medicinalProduct?.id);
        if (!Number.isFinite(medicinalProductId)) return undefined;

        (async () => {
            try {
                const { data } = await prescriptionsApi.getMedicationPackList(
                    medicinalProductId,
                );
                setMedicationPacks(data.result);
            } catch (error) {
                console.error(error);
            }
        })();
    }, [formik.values.medicinalProduct]);

    const handleChangeMedicationPack = (event: ChangeEventValue) => {
        formik.setFieldValue('medicationPackId', event.target.value);
        const selectedPack = medicationPacks.find(pack => pack.id === event.target.value);
        if (selectedPack?.isReimbursed) {
            formik.setFieldValue('dosageMode', DOSAGE_MODES.EXTENDED);
        }
    };

    const handleChangeDosageMode = (mode: DosageModeOption) => {
        formik.setFieldValue('dosageMode', mode);
        if (mode === DOSAGE_MODES.EXTENDED) return;

        formik.setFieldValue('treatmentDuration', '');
        formik.setFieldValue('dosageFrequencyOption', '');
        formik.setFieldValue('dosageIntervalHours', '');
        formik.setFieldValue('dosageAmount', '');
        formik.setFieldValue('dosageUnit', '');
    };

    return {
        selectedMedicationPack,
        medicationPackDosageUnits,
        hasPredefinedDosageUnits,
        isReimbursementDisabled,
        handleChangeMedicationPack,
        handleChangeDosageMode,
    };
};

export default usePrescriptionFormShared;